import { ArrowUpRight, Phone } from "lucide-react";
import { motion, useReducedMotion } from "framer-motion";

const HERO_IMG =
    "https://static.prod-images.emergentagent.com/jobs/8e02870b-796d-47e6-9225-636f1c3ce92e/images/5ae86783d2f0fa56649c59db9ce3b98a941b62a69be0d88d526c9e7b78fa4622.png";

const EASE = [0.22, 0.61, 0.22, 1];

export default function Hero() {
    const reduce = useReducedMotion();

    const container = {
        hidden: {},
        show: {
            transition: { staggerChildren: reduce ? 0 : 0.12, delayChildren: 0.15 },
        },
    };
    const item = {
        hidden: { opacity: 0, y: reduce ? 0 : 34 },
        show: { opacity: 1, y: 0, transition: { duration: 0.9, ease: EASE } },
    };
    const imageItem = {
        hidden: { opacity: 0, scale: reduce ? 1 : 1.04, y: reduce ? 0 : 24 },
        show: { opacity: 1, scale: 1, y: 0, transition: { duration: 1.1, ease: EASE } },
    };

    return (
        <section
            id="top"
            className="relative pt-28 md:pt-32 pb-20 md:pb-32 px-6 md:px-10 overflow-hidden"
            data-testid="hero-section"
        >
            {/* Glow */}
            <div className="pointer-events-none absolute -top-32 -right-40 w-[700px] h-[700px] hero-glow" aria-hidden />
            <div className="pointer-events-none absolute top-40 -left-20 w-[400px] h-[400px] rounded-full bg-[#D4A017] opacity-40 blur-3xl" aria-hidden />

            <motion.div
                className="relative max-w-7xl mx-auto grid md:grid-cols-12 gap-10 md:gap-8 items-center min-h-[80vh]"
                variants={container}
                initial="hidden"
                animate="show"
            >
                {/* Left text */}
                <div className="md:col-span-7 relative z-10">
                    <motion.div variants={item} className="flex items-center gap-3 mb-7">
                        <span className="inline-flex items-center gap-2 bg-[#D4A017] border-2 border-black rounded-full px-3 py-1 text-[10px] font-mono uppercase tracking-widest font-bold">
                            <span className="w-1.5 h-1.5 rounded-full bg-[#C2410C] animate-glow-pulse" />
                            A boutique artist label
                        </span>
                    </motion.div>

                    <motion.h1
                        variants={item}
                        className="font-display tracking-tighter font-black leading-[0.92] text-[15vw] sm:text-[12vw] md:text-[8.4vw] lg:text-[7.6rem]"
                        data-testid="hero-heading"
                    >
                        <span className="block">My First</span>
                        <span className="block">
                            <em className="not-italic relative inline-block">
                                <span className="relative z-10 px-2 text-white">Single</span>
                                <motion.span
                                    className="absolute inset-0 bg-[#C2410C] -rotate-1 z-0"
                                    aria-hidden
                                    initial={{ scaleX: reduce ? 1 : 0 }}
                                    animate={{ scaleX: 1 }}
                                    transition={{ duration: 0.9, ease: EASE, delay: 0.6 }}
                                    style={{ transformOrigin: "left center" }}
                                />
                            </em>
                            <span className="inline-block ml-3 align-text-top text-[#C2410C]">.</span>
                        </span>
                    </motion.h1>

                    <motion.p
                        variants={item}
                        className="mt-7 text-lg md:text-xl text-stone-700 max-w-xl leading-relaxed"
                    >
                        From singing lessons to Worldwide release. We help everyday
                        people become real recording artists with professional
                        coaching, advanced music production technology and
                        industry‑standard distribution.
                    </motion.p>

                    <motion.div variants={item} className="mt-10 flex flex-col sm:flex-row gap-4">
                        <a
                            href="#teachers"
                            className="group inline-flex items-center justify-center gap-2 bg-[#0A0A0A] hover:bg-[#C2410C] text-white font-medium rounded-full px-7 py-4 transition-all active:scale-95 border-2 border-black shadow-[5px_5px_0_0_#C2410C] hover:shadow-[2px_2px_0_0_#C2410C] hover:translate-x-[3px] hover:translate-y-[3px]"
                            data-testid="hero-cta-start"
                        >
                            Start your artist journey
                            <ArrowUpRight size={18} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                        </a>
                        <a
                            href="#teachers"
                            className="inline-flex items-center justify-center gap-2 bg-[#D4A017] hover:bg-[#B88810] text-black font-medium rounded-full px-7 py-4 transition-all active:scale-95 border-2 border-black shadow-[5px_5px_0_0_#0A0A0A] hover:shadow-[2px_2px_0_0_#0A0A0A] hover:translate-x-[3px] hover:translate-y-[3px]"
                            data-testid="hero-cta-book"
                        >
                            <Phone size={16} />
                            Book a discovery call
                        </a>
                    </motion.div>

                    <motion.div variants={item} className="mt-12 flex flex-wrap items-center gap-x-8 gap-y-3 text-sm text-stone-700">
                        <span className="flex items-center gap-2">
                            <span className="w-1.5 h-1.5 bg-[#C2410C] rounded-full animate-glow-pulse" />
                            No experience needed
                        </span>
                        <span className="flex items-center gap-2">
                            <span className="w-1.5 h-1.5 bg-[#1B4D3E] rounded-full animate-glow-pulse" />
                            Released on every major platform
                        </span>
                    </motion.div>
                </div>

                {/* Right image */}
                <motion.div variants={imageItem} className="md:col-span-5 relative h-[440px] md:h-[600px]">
                    {/* Yellow offset block */}
                    <div className="absolute -inset-3 bg-[#D4A017] rounded-3xl rotate-2 border-2 border-black" aria-hidden />
                    <div className="absolute inset-0 rounded-3xl overflow-hidden border-2 border-black z-10">
                        <img
                            src={HERO_IMG}
                            alt="Young artist recording in a moody studio"
                            className="w-full h-full object-cover"
                            loading="eager"
                            data-testid="hero-image"
                        />
                    </div>

                    {/* Floating overlay card */}
                    <motion.div
                        className="absolute bottom-5 left-5 right-5 md:bottom-6 md:left-6 md:right-auto md:w-[280px] bg-white border-2 border-black rounded-2xl p-4 shadow-[4px_4px_0_0_#0A0A0A] z-20"
                        initial={{ opacity: 0, y: reduce ? 0 : 24 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, ease: EASE, delay: 1.1 }}
                    >
                        <div className="flex items-center justify-end text-[#C2410C]">
                            <span className="flex gap-[3px] items-end h-4">
                                <span className="eq-bar" />
                                <span className="eq-bar" />
                                <span className="eq-bar" />
                                <span className="eq-bar" />
                            </span>
                        </div>
                        <div className="mt-2 font-display text-lg font-bold leading-tight text-black">
                            Your debut single
                        </div>
                        <div className="text-xs text-stone-600 mt-0.5">
                            by you · produced with us
                        </div>
                        <div className="mt-3 h-1.5 bg-black/10 rounded-full overflow-hidden">
                            <motion.div
                                className="h-full bg-[#C2410C]"
                                initial={{ width: 0 }}
                                animate={{ width: "66%" }}
                                transition={{ duration: 1.4, ease: EASE, delay: 1.5 }}
                            />
                        </div>
                        <div className="mt-1.5 flex justify-between text-[10px] text-stone-500 font-mono">
                            <span>1:42</span>
                            <span>2:51</span>
                        </div>
                    </motion.div>
                </motion.div>
            </motion.div>

            {/* Marquee strip */}
            <motion.div
                className="mt-20 md:mt-24 max-w-7xl mx-auto bg-[#0A0A0A] text-[#D4A017] border-2 border-black rounded-full py-4 overflow-hidden"
                initial={{ opacity: 0, y: reduce ? 0 : 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.9, ease: EASE, delay: 1.3 }}
            >
                <div className="flex gap-12 whitespace-nowrap animate-[marquee_38s_linear_infinite] eyebrow text-[#D4A017]" style={{ color: "#D4A017" }}>
                    {Array.from({ length: 2 }).map((_, i) => (
                        <span key={i} className="flex gap-12">
                            <span>★ Spotify</span>
                            <span>★ Apple Music</span>
                            <span>★ YouTube Music</span>
                            <span>★ TikTok</span>
                            <span>★ Amazon Music</span>
                            <span>★ Deezer</span>
                            <span>★ Tidal</span>
                        </span>
                    ))}
                </div>
            </motion.div>

            <style>{`
                @keyframes marquee {
                    from { transform: translateX(0); }
                    to { transform: translateX(-50%); }
                }
            `}</style>
        </section>
    );
}
